import _extends from '@babel/runtime/helpers/extends';
import React, { useRef, useEffect, useMemo, createContext, useContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { p as props, d as defaults } from './componentEl-fcbc301e.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-1ed461aa.js';
import { g as getPadding, b as addMarginClasses, m as marginPropTypes } from './spacing-0f39ba4a.js';
import { G as Group } from './Group-0add3181.js';
import { r as reactResponsive, b as breakpoints } from './breakpoints-9c323f1e.js';
import useAnimatedModalState from './useAnimatedModalState.js';
import { z as zIndexProps } from './zIndex-60238fbe.js';
import { T as Token } from './Token-352a77f8.js';
import { T as Typography } from './Typography-98688343.js';
import { D as Dropdown } from './Dropdown-3c030034.js';
import useDropdownState from './useDropdownState.js';
import { R as Radio } from './Radio-89e45f54.js';
import { W as Wrapper } from './Wrapper-2eba1576.js';
import { R as Responsive } from './Responsive-ab9efc3e.js';
import IconCaretRight from './IconCaretRight.js';
import IconCaretDown from './IconCaretDown.js';
import { F as FormLabel } from './FormLabel-6bc29108.js';
import { L as LogoFallback } from './LogoFallback-4ce82dbd.js';
import { A as Avatar } from './Avatar-548d36a9.js';
import { B as Button } from './Button-e85764bc.js';
import { D as Divider } from './Divider-a8e7ad8e.js';
import { N as Notifications } from './Notifications-3d7f02ba.js';
import IconBell from './IconBell.js';
import { B as Box } from './Box-a46c799c.js';
import useModalPortal from './useModalPortal.js';
import { M as Modal } from './Modal-506edefb.js';
import useModalState from './useModalState.js';
import IconDotGrid from './IconDotGrid.js';
import IconGear from './IconGear.js';
import IconInfo from './IconInfo.js';
import { S as Sticky } from './Sticky-006fc972.js';
import IconMenu from './IconMenu.js';

var resizeObservers = [];

var hasActiveObservations = function () {
    return resizeObservers.some(function (ro) { return ro.activeTargets.length > 0; });
};

var hasSkippedObservations = function () {
    return resizeObservers.some(function (ro) { return ro.skippedTargets.length > 0; });
};

var msg = 'ResizeObserver loop completed with undelivered notifications.';
var deliverResizeLoopError = function () {
    var event;
    if (typeof ErrorEvent === 'function') {
        event = new ErrorEvent('error', {
            message: msg
        });
    }
    else {
        event = document.createEvent('Event');
        event.initEvent('error', false, false);
        event.message = msg;
    }
    window.dispatchEvent(event);
};

var ResizeObserverBoxOptions;
(function (ResizeObserverBoxOptions) {
    ResizeObserverBoxOptions["BORDER_BOX"] = "border-box";
    ResizeObserverBoxOptions["CONTENT_BOX"] = "content-box";
    ResizeObserverBoxOptions["DEVICE_PIXEL_CONTENT_BOX"] = "device-pixel-content-box";
})(ResizeObserverBoxOptions || (ResizeObserverBoxOptions = {}));

var freeze = function (obj) { return Object.freeze(obj); };

var ResizeObserverSize = (function () {
    function ResizeObserverSize(inlineSize, blockSize) {
        this.inlineSize = inlineSize;
        this.blockSize = blockSize;
        freeze(this);
    }
    return ResizeObserverSize;
}());

var DOMRectReadOnly = (function () {
    function DOMRectReadOnly(x, y, width, height) {
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.top = this.y;
        this.left = this.x;
        this.bottom = this.top + this.height;
        this.right = this.left + this.width;
        return freeze(this);
    }
    DOMRectReadOnly.prototype.toJSON = function () {
        var _a = this, x = _a.x, y = _a.y, top = _a.top, right = _a.right, bottom = _a.bottom, left = _a.left, width = _a.width, height = _a.height;
        return { x: x, y: y, top: top, right: right, bottom: bottom, left: left, width: width, height: height };
    };
    DOMRectReadOnly.fromRect = function (rectangle) {
        return new DOMRectReadOnly(rectangle.x, rectangle.y, rectangle.width, rectangle.height);
    };
    return DOMRectReadOnly;
}());

var isSVG = function (target) { return target instanceof SVGElement && 'getBBox' in target; };
var isHidden = function (target) {
    if (isSVG(target)) {
        var _a = target.getBBox(), width = _a.width, height = _a.height;
        return !width && !height;
    }
    var _b = target, offsetWidth = _b.offsetWidth, offsetHeight = _b.offsetHeight;
    return !(offsetWidth || offsetHeight || target.getClientRects().length);
};
var isElement = function (obj) {
    var _a;
    if (obj instanceof Element) {
        return true;
    }
    var scope = (_a = obj === null || obj === void 0 ? void 0 : obj.ownerDocument) === null || _a === void 0 ? void 0 : _a.defaultView;
    return !!(scope && obj instanceof scope.Element);
};
var isReplacedElement = function (target) {
    switch (target.tagName) {
        case 'INPUT':
            if (target.type !== 'image') {
                break;
            }
        case 'VIDEO':
        case 'AUDIO':
        case 'EMBED':
        case 'OBJECT':
        case 'CANVAS':
        case 'IFRAME':
        case 'IMG':
            return true;
    }
    return false;
};

var global = typeof window !== 'undefined' ? window : {};

var cache = new WeakMap();
var scrollRegexp = /auto|scroll/;
var verticalRegexp = /^tb|vertical/;
var IE = (/msie|trident/i).test(global.navigator && global.navigator.userAgent);
var parseDimension = function (pixel) { return parseFloat(pixel || '0'); };
var size = function (inlineSize, blockSize, switchSizes) {
    if (inlineSize === void 0) { inlineSize = 0; }
    if (blockSize === void 0) { blockSize = 0; }
    if (switchSizes === void 0) { switchSizes = false; }
    return new ResizeObserverSize((switchSizes ? blockSize : inlineSize) || 0, (switchSizes ? inlineSize : blockSize) || 0);
};
var zeroBoxes = freeze({
    devicePixelContentBoxSize: size(),
    borderBoxSize: size(),
    contentBoxSize: size(),
    contentRect: new DOMRectReadOnly(0, 0, 0, 0)
});
var calculateBoxSizes = function (target, forceRecalculation) {
    if (forceRecalculation === void 0) { forceRecalculation = false; }
    if (cache.has(target) && !forceRecalculation) {
        return cache.get(target);
    }
    if (isHidden(target)) {
        cache.set(target, zeroBoxes);
        return zeroBoxes;
    }
    var cs = getComputedStyle(target);
    var svg = isSVG(target) && target.ownerSVGElement && target.getBBox();
    var removePadding = !IE && cs.boxSizing === 'border-box';
    var switchSizes = verticalRegexp.test(cs.writingMode || '');
    var canScrollVertically = !svg && scrollRegexp.test(cs.overflowY || '');
    var canScrollHorizontally = !svg && scrollRegexp.test(cs.overflowX || '');
    var paddingTop = svg ? 0 : parseDimension(cs.paddingTop);
    var paddingRight = svg ? 0 : parseDimension(cs.paddingRight);
    var paddingBottom = svg ? 0 : parseDimension(cs.paddingBottom);
    var paddingLeft = svg ? 0 : parseDimension(cs.paddingLeft);
    var borderTop = svg ? 0 : parseDimension(cs.borderTopWidth);
    var borderRight = svg ? 0 : parseDimension(cs.borderRightWidth);
    var borderBottom = svg ? 0 : parseDimension(cs.borderBottomWidth);
    var borderLeft = svg ? 0 : parseDimension(cs.borderLeftWidth);
    var horizontalPadding = paddingLeft + paddingRight;
    var verticalPadding = paddingTop + paddingBottom;
    var horizontalBorderArea = borderLeft + borderRight;
    var verticalBorderArea = borderTop + borderBottom;
    var horizontalScrollbarThickness = !canScrollHorizontally ? 0 : target.offsetHeight - verticalBorderArea - target.clientHeight;
    var verticalScrollbarThickness = !canScrollVertically ? 0 : target.offsetWidth - horizontalBorderArea - target.clientWidth;
    var widthReduction = removePadding ? horizontalPadding + horizontalBorderArea : 0;
    var heightReduction = removePadding ? verticalPadding + verticalBorderArea : 0;
    var contentWidth = svg ? svg.width : parseDimension(cs.width) - widthReduction - verticalScrollbarThickness;
    var contentHeight = svg ? svg.height : parseDimension(cs.height) - heightReduction - horizontalScrollbarThickness;
    var borderBoxWidth = contentWidth + horizontalPadding + verticalScrollbarThickness + horizontalBorderArea;
    var borderBoxHeight = contentHeight + verticalPadding + horizontalScrollbarThickness + verticalBorderArea;
    var boxes = freeze({
        devicePixelContentBoxSize: size(Math.round(contentWidth * devicePixelRatio), Math.round(contentHeight * devicePixelRatio), switchSizes),
        borderBoxSize: size(borderBoxWidth, borderBoxHeight, switchSizes),
        contentBoxSize: size(contentWidth, contentHeight, switchSizes),
        contentRect: new DOMRectReadOnly(paddingLeft, paddingTop, contentWidth, contentHeight)
    });
    cache.set(target, boxes);
    return boxes;
};
var calculateBoxSize = function (target, observedBox, forceRecalculation) {
    var _a = calculateBoxSizes(target, forceRecalculation), borderBoxSize = _a.borderBoxSize, contentBoxSize = _a.contentBoxSize, devicePixelContentBoxSize = _a.devicePixelContentBoxSize;
    switch (observedBox) {
        case ResizeObserverBoxOptions.DEVICE_PIXEL_CONTENT_BOX:
            return devicePixelContentBoxSize;
        case ResizeObserverBoxOptions.BORDER_BOX:
            return borderBoxSize;
        default:
            return contentBoxSize;
    }
};

var ResizeObserverEntry = (function () {
    function ResizeObserverEntry(target) {
        var boxes = calculateBoxSizes(target);
        this.target = target;
        this.contentRect = boxes.contentRect;
        this.borderBoxSize = freeze([boxes.borderBoxSize]);
        this.contentBoxSize = freeze([boxes.contentBoxSize]);
        this.devicePixelContentBoxSize = freeze([boxes.devicePixelContentBoxSize]);
    }
    return ResizeObserverEntry;
}());

var calculateDepthForNode = function (node) {
    if (isHidden(node)) {
        return Infinity;
    }
    var depth = 0;
    var parent = node.parentNode;
    while (parent) {
        depth += 1;
        parent = parent.parentNode;
    }
    return depth;
};

var broadcastActiveObservations = function () {
    var shallowestDepth = Infinity;
    var callbacks = [];
    resizeObservers.forEach(function processObserver(ro) {
        if (ro.activeTargets.length === 0) {
            return;
        }
        var entries = [];
        ro.activeTargets.forEach(function processTarget(ot) {
            var entry = new ResizeObserverEntry(ot.target);
            var targetDepth = calculateDepthForNode(ot.target);
            entries.push(entry);
            ot.lastReportedSize = calculateBoxSize(ot.target, ot.observedBox);
            if (targetDepth < shallowestDepth) {
                shallowestDepth = targetDepth;
            }
        });
        callbacks.push(function resizeObserverCallback() {
            ro.callback.call(ro.observer, entries, ro.observer);
        });
        ro.activeTargets.splice(0, ro.activeTargets.length);
    });
    for (var _i = 0, callbacks_1 = callbacks; _i < callbacks_1.length; _i++) {
        var callback = callbacks_1[_i];
        callback();
    }
    return shallowestDepth;
};

var gatherActiveObservationsAtDepth = function (depth) {
    resizeObservers.forEach(function processObserver(ro) {
        ro.activeTargets.splice(0, ro.activeTargets.length);
        ro.skippedTargets.splice(0, ro.skippedTargets.length);
        ro.observationTargets.forEach(function processTarget(ot) {
            if (ot.isActive()) {
                if (calculateDepthForNode(ot.target) > depth) {
                    ro.activeTargets.push(ot);
                }
                else {
                    ro.skippedTargets.push(ot);
                }
            }
        });
    });
};

var process = function () {
    var depth = 0;
    gatherActiveObservationsAtDepth(depth);
    while (hasActiveObservations()) {
        depth = broadcastActiveObservations();
        gatherActiveObservationsAtDepth(depth);
    }
    if (hasSkippedObservations()) {
        deliverResizeLoopError();
    }
    return depth > 0;
};

var trigger;
var callbacks = [];
var notify = function () { return callbacks.splice(0).forEach(function (cb) { return cb(); }); };
var queueMicroTask = function (callback) {
    if (!trigger) {
        var toggle_1 = 0;
        var el_1 = document.createTextNode('');
        var config = { characterData: true };
        new MutationObserver(function () { return notify(); }).observe(el_1, config);
        trigger = function () { el_1.textContent = "".concat(toggle_1 ? toggle_1-- : toggle_1++); };
    }
    callbacks.push(callback);
    trigger();
};

var queueResizeObserver = function (cb) {
    queueMicroTask(function ResizeObserver() {
        requestAnimationFrame(cb);
    });
};

var watching = 0;
var isWatching = function () { return !!watching; };
var CATCH_PERIOD = 250;
var observerConfig = { attributes: true, characterData: true, childList: true, subtree: true };
var events = [
    'resize',
    'load',
    'transitionend',
    'animationend',
    'animationstart',
    'animationiteration',
    'keyup',
    'keydown',
    'mouseup',
    'mousedown',
    'mouseover',
    'mouseout',
    'blur',
    'focus'
];
var time = function (timeout) {
    if (timeout === void 0) { timeout = 0; }
    return Date.now() + timeout;
};
var scheduled = false;
var Scheduler = (function () {
    function Scheduler() {
        var _this = this;
        this.stopped = true;
        this.listener = function () { return _this.schedule(); };
    }
    Scheduler.prototype.run = function (timeout) {
        var _this = this;
        if (timeout === void 0) { timeout = CATCH_PERIOD; }
        if (scheduled) {
            return;
        }
        scheduled = true;
        var until = time(timeout);
        queueResizeObserver(function () {
            var elementsHaveResized = false;
            try {
                elementsHaveResized = process();
            }
            finally {
                scheduled = false;
                timeout = until - time();
                if (!isWatching()) {
                    return;
                }
                if (elementsHaveResized) {
                    _this.run(1000);
                }
                else if (timeout > 0) {
                    _this.run(timeout);
                }
                else {
                    _this.start();
                }
            }
        });
    };
    Scheduler.prototype.schedule = function () {
        this.stop();
        this.run();
    };
    Scheduler.prototype.observe = function () {
        var _this = this;
        var cb = function () { return _this.observer && _this.observer.observe(document.body, observerConfig); };
        document.body ? cb() : global.addEventListener('DOMContentLoaded', cb);
    };
    Scheduler.prototype.start = function () {
        var _this = this;
        if (this.stopped) {
            this.stopped = false;
            this.observer = new MutationObserver(this.listener);
            this.observe();
            events.forEach(function (name) { return global.addEventListener(name, _this.listener, true); });
        }
    };
    Scheduler.prototype.stop = function () {
        var _this = this;
        if (!this.stopped) {
            this.observer && this.observer.disconnect();
            events.forEach(function (name) { return global.removeEventListener(name, _this.listener, true); });
            this.stopped = true;
        }
    };
    return Scheduler;
}());
var scheduler = new Scheduler();
var updateCount = function (n) {
    !watching && n > 0 && scheduler.start();
    watching += n;
    !watching && scheduler.stop();
};

var skipNotifyOnElement = function (target) {
    return !isSVG(target)
        && !isReplacedElement(target)
        && getComputedStyle(target).display === 'inline';
};
var ResizeObservation = (function () {
    function ResizeObservation(target, observedBox) {
        this.target = target;
        this.observedBox = observedBox || ResizeObserverBoxOptions.CONTENT_BOX;
        this.lastReportedSize = {
            inlineSize: 0,
            blockSize: 0
        };
    }
    ResizeObservation.prototype.isActive = function () {
        var size = calculateBoxSize(this.target, this.observedBox, true);
        if (skipNotifyOnElement(this.target)) {
            this.lastReportedSize = size;
        }
        if (this.lastReportedSize.inlineSize !== size.inlineSize
            || this.lastReportedSize.blockSize !== size.blockSize) {
            return true;
        }
        return false;
    };
    return ResizeObservation;
}());

var ResizeObserverDetail = (function () {
    function ResizeObserverDetail(resizeObserver, callback) {
        this.activeTargets = [];
        this.skippedTargets = [];
        this.observationTargets = [];
        this.observer = resizeObserver;
        this.callback = callback;
    }
    return ResizeObserverDetail;
}());

var observerMap = new WeakMap();
var getObservationIndex = function (observationTargets, target) {
    for (var i = 0; i < observationTargets.length; i += 1) {
        if (observationTargets[i].target === target) {
            return i;
        }
    }
    return -1;
};
var ResizeObserverController = (function () {
    function ResizeObserverController() {
    }
    ResizeObserverController.connect = function (resizeObserver, callback) {
        var detail = new ResizeObserverDetail(resizeObserver, callback);
        observerMap.set(resizeObserver, detail);
    };
    ResizeObserverController.observe = function (resizeObserver, target, options) {
        var detail = observerMap.get(resizeObserver);
        var firstObservation = detail.observationTargets.length === 0;
        if (getObservationIndex(detail.observationTargets, target) < 0) {
            firstObservation && resizeObservers.push(detail);
            detail.observationTargets.push(new ResizeObservation(target, options && options.box));
            updateCount(1);
            scheduler.schedule();
        }
    };
    ResizeObserverController.unobserve = function (resizeObserver, target) {
        var detail = observerMap.get(resizeObserver);
        var index = getObservationIndex(detail.observationTargets, target);
        var lastObservation = detail.observationTargets.length === 1;
        if (index >= 0) {
            lastObservation && resizeObservers.splice(resizeObservers.indexOf(detail), 1);
            detail.observationTargets.splice(index, 1);
            updateCount(-1);
        }
    };
    ResizeObserverController.disconnect = function (resizeObserver) {
        var _this = this;
        var detail = observerMap.get(resizeObserver);
        detail.observationTargets.slice().forEach(function (ot) { return _this.unobserve(resizeObserver, ot.target); });
        detail.activeTargets.splice(0, detail.activeTargets.length);
    };
    return ResizeObserverController;
}());

var ResizeObserver = (function () {
    function ResizeObserver(callback) {
        if (arguments.length === 0) {
            throw new TypeError("Failed to construct 'ResizeObserver': 1 argument required, but only 0 present.");
        }
        if (typeof callback !== 'function') {
            throw new TypeError("Failed to construct 'ResizeObserver': The callback provided as parameter 1 is not a function.");
        }
        ResizeObserverController.connect(this, callback);
    }
    ResizeObserver.prototype.observe = function (target, options) {
        if (arguments.length === 0) {
            throw new TypeError("Failed to execute 'observe' on 'ResizeObserver': 1 argument required, but only 0 present.");
        }
        if (!isElement(target)) {
            throw new TypeError("Failed to execute 'observe' on 'ResizeObserver': parameter 1 is not of type 'Element");
        }
        ResizeObserverController.observe(this, target, options);
    };
    ResizeObserver.prototype.unobserve = function (target) {
        if (arguments.length === 0) {
            throw new TypeError("Failed to execute 'unobserve' on 'ResizeObserver': 1 argument required, but only 0 present.");
        }
        if (!isElement(target)) {
            throw new TypeError("Failed to execute 'unobserve' on 'ResizeObserver': parameter 1 is not of type 'Element");
        }
        ResizeObserverController.unobserve(this, target);
    };
    ResizeObserver.prototype.disconnect = function () {
        ResizeObserverController.disconnect(this);
    };
    ResizeObserver.toString = function () {
        return 'function ResizeObserver () { [polyfill code] }';
    };
    return ResizeObserver;
}());

var t=React["undefined"!=typeof document&&void 0!==document.createElement?"useLayoutEffect":"useEffect"];var t$1 = t;

var n = e=>{var a=useRef(e);return useEffect(()=>{a.current=e;}),a};

function e(){}var o,i="undefined"!=typeof window&&"ResizeObserver"in window?window.ResizeObserver:ResizeObserver;function useResizeObserver(r,s){var u,a,v,c,f=o||(u=0,a=[],v=new Map,c=new i((e,r)=>{a=a.concat(e),u||window.requestAnimationFrame((function(){for(var e=new Set,t=function(t){if(e.has(a[t].target))return "continue";e.add(a[t].target);var n=v.get(a[t].target);null==n||n.forEach(e=>e(a[t],r));},n=0;n<a.length;n++)t(n);a=[],u=0;})),u=1;}),o={observer:c,subscribe(e,r){var t;c.observe(e);var n=null!==(t=v.get(e))&&void 0!==t?t:[];n.push(r),v.set(e,n);},unsubscribe(e,r){var t,n=null!==(t=v.get(e))&&void 0!==t?t:[];if(1===n.length)return c.unobserve(e),void v.delete(e);var o=n.indexOf(r);-1!==o&&n.splice(o,1),v.set(e,n);}}),b=n(s);return t$1(()=>{function t(e,r){n||b.current(e,r);}var n=0,o=r&&"current"in r?r.current:r;return o?(f.subscribe(o,t),()=>{n=1,f.unsubscribe(o,t);}):e},[r,f,b]),f.observer}

const HeaderBreakpointsContext = /*#__PURE__*/createContext();
function HeaderBreakpointsProvider({
  desktop,
  mobile,
  children
}) {
  const value = useMemo(() => ({
    desktop,
    mobile
  }), [desktop, mobile]);
  return /*#__PURE__*/React.createElement(HeaderBreakpointsContext.Provider, {
    value: value
  }, children);
}
HeaderBreakpointsProvider.propTypes = {
  desktop: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool
  }),
  mobile: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool
  })
};
HeaderBreakpointsProvider.defaultProps = {
  desktop: {
    md: true,
    lg: true
  },
  mobile: {
    xs: true,
    sm: true
  }
};
function useHeaderBreakpoints() {
  const context = useContext(HeaderBreakpointsContext);
  if (context === undefined) {
    throw new Error('useHeaderBreakpoints must be used within a HeaderBreakpointsProvider');
  }
  return context;
}

const HeaderContext = /*#__PURE__*/createContext();
function HeaderProvider({
  children,
  initialContainerHeight
}) {
  const {
    open: isDrawerOpen,
    status: drawerStatus,
    componentRef: drawerRef,
    openModal: openDrawer,
    closeModal: closeDrawer
  } = useAnimatedModalState({
    status: 'closed'
  });

  // although this is init here
  // the source of truth is the header component
  // it was moved there bc of issues in the NextJS Auth Wrapper
  const [containerHeight, setContainerHeight] = useState(initialContainerHeight);
  const isSm = reactResponsive.exports.useMediaQuery({
    query: `(max-width: ${breakpoints.sm.max})`
  });

  // event listener to close drawer when user clicks outside in mobile
  const onClickOutside = useCallback(e => {
    if (isDrawerOpen && !drawerRef?.current?.contains(e.target)) {
      closeDrawer();
    }
  }, [closeDrawer, isDrawerOpen, drawerRef]);
  const toggleDrawer = useCallback(() => {
    if (!isDrawerOpen) {
      openDrawer();
    } else {
      closeDrawer();
    }
  }, [closeDrawer, openDrawer, isDrawerOpen]);
  useEffect(() => {
    if (isSm) {
      window.addEventListener('pointerdown', onClickOutside);
    }
    return () => window.removeEventListener('pointerdown', onClickOutside);
  }, [onClickOutside, isSm]); // listener for when user clicks outside dropdown

  const value = useMemo(() => ({
    drawerStatus,
    drawerRef,
    openDrawer,
    closeDrawer,
    toggleDrawer,
    isDrawerOpen,
    containerHeight,
    setContainerHeight
  }), [drawerStatus, drawerRef, openDrawer, closeDrawer, toggleDrawer, isDrawerOpen, containerHeight, setContainerHeight]);
  return /*#__PURE__*/React.createElement(HeaderContext.Provider, {
    value: value
  }, children);
}
HeaderProvider.propTypes = {
  /** initial value for containerHeight in case it takes a long time to generate via JS */
  initialContainerHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};
HeaderProvider.defaultProps = {
  initialContainerHeight: 'var(--st-header-height)'
};
function useHeader() {
  const context = useContext(HeaderContext);
  if (context === undefined) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }
  return context;
}

/**
Component to render app name and environment
*/
const Name = /*#__PURE__*/React.forwardRef(({
  className,
  componentEl: Component,
  children,
  env,
  ...otherProps
}, ref) => {
  let cssClasses = ['st_header__name st_header__item'];
  cssClasses = addClassNames(className, cssClasses);
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps, {
    ref: ref
  }), /*#__PURE__*/React.createElement(Group, {
    direction: "row",
    gap: "sm",
    fullHeight: true,
    margin: {
      left: 'md'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    size: "inherit"
  }, children), env && /*#__PURE__*/React.createElement(Token, {
    caps: true,
    className: "st_header__env",
    color: "blurple"
  }, env)));
});
Name.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** env of app (for token) */
  env: PropTypes.string
};
Name.defaultProps = {
  className: null,
  componentEl: 'div',
  env: null
};
Name.displayName = 'Header.Name';
var Name$1 = Name;

/**
Component to render app name and environment
*/
function Property({
  className,
  componentEl: Component,
  options,
  name,
  image: ImageElement,
  onSwitch,
  ...otherProps
}) {
  const {
    open,
    componentRef,
    toggleDropdown
  } = useDropdownState();
  const [accordionState, setAccordionState] = useState(false);
  const {
    desktop,
    mobile
  } = useHeaderBreakpoints();
  let trigger = null;
  if (ImageElement) {
    trigger = /*#__PURE__*/React.createElement(ImageElement, {
      size: "lg"
    });
  } else if (name) {
    // fallback when user doesn't supply an image
    // and passes the product name
    trigger = /*#__PURE__*/React.createElement(LogoFallback, {
      text: name,
      maxlen: 1,
      maxlines: 1
    });
  }

  // calls on the onSwitch prop when user clicks on a product
  // adding this so we can pass the value of the selected product
  // rather than a click event
  const localSwitch = useCallback(e => {
    if (onSwitch) {
      if (e.target.checked) onSwitch(e.target.value);
    }
  }, [onSwitch]);

  // if there's no logo to display, don't render
  if (!trigger) return null;

  // flag to render dropdown or just logo
  const hasOptions = options && options?.length > 1;
  return /*#__PURE__*/React.createElement(React.Fragment, null, hasOptions && /*#__PURE__*/React.createElement(Responsive, mobile, /*#__PURE__*/React.createElement("div", _extends({
    className: addClassNames(className, []).join(' ')
  }, otherProps), /*#__PURE__*/React.createElement("details", {
    onToggle: e => setAccordionState(e.target.open)
  }, /*#__PURE__*/React.createElement("summary", {
    className: "st_header_propertyaccordiontoggle",
    "aria-label": "Current Property"
  }, /*#__PURE__*/React.createElement(Group, {
    direction: "row",
    gap: "sm",
    className: "st_focusable st_clickable"
  }, /*#__PURE__*/React.createElement(Group, {
    align: "center",
    valign: "center",
    className: "st_header__propertylogo"
  }, trigger), /*#__PURE__*/React.createElement(Group.Item, {
    grow: 1
  }, name), /*#__PURE__*/React.createElement(Group, {
    direction: "row",
    align: "end",
    margin: {
      right: 'mlg'
    }
  }, accordionState === true ? /*#__PURE__*/React.createElement(IconCaretDown, null) : /*#__PURE__*/React.createElement(IconCaretRight, null)))), /*#__PURE__*/React.createElement(Wrapper, {
    padding: {
      horizontal: 'md',
      top: 'none',
      bottom: 'mlg'
    }
  }, /*#__PURE__*/React.createElement(Group, {
    direction: "column",
    gap: "sm"
  }, options.map(p => /*#__PURE__*/React.createElement(FormLabel, {
    label: p.name,
    labelPosition: "right",
    fullWidth: true,
    key: p.value
  }, /*#__PURE__*/React.createElement(Radio, {
    name: `header_product_${p.value}`,
    id: `header_product_${p.value}`,
    onChange: localSwitch,
    value: p.value,
    checked: p.name === name
  })))))))), /*#__PURE__*/React.createElement(Responsive, desktop, /*#__PURE__*/React.createElement(Component, _extends({
    className: addClassNames(className, ['st_header__property', 'st_header__item']).join(' ')
  }, otherProps, {
    "aria-label": `Current Property: ${name}`
  }), hasOptions && /*#__PURE__*/React.createElement(Dropdown, {
    position: "none",
    fullHeight: true,
    open: open,
    "aria-expanded": !!open,
    componentRef: componentRef,
    onToggle: toggleDropdown
  }, /*#__PURE__*/React.createElement(Dropdown.Trigger, {
    fullHeight: true,
    "aria-label": "Switch Property"
  }, /*#__PURE__*/React.createElement(Group, {
    align: "center",
    valign: "center",
    componentEl: "button",
    fullHeight: true,
    className: ['st_focusable st_header__hoverable st_header__propertylogo', open ? 'st_header__hoverable--active' : ''].join(' ')
  }, trigger)), /*#__PURE__*/React.createElement(Dropdown.Content, {
    className: "st_header__dropdown st_header__propertydropdown",
    padding: {
      all: 'smd'
    }
  }, options.map(p => /*#__PURE__*/React.createElement(FormLabel, {
    label: p.name,
    labelPosition: "right",
    fullWidth: true,
    key: p.value
  }, /*#__PURE__*/React.createElement(Radio, {
    name: `header_product_${p.value}`,
    id: `header_product_${p.value}`,
    onChange: localSwitch,
    value: p.value,
    checked: p.name === name
  }))))), !hasOptions && /*#__PURE__*/React.createElement(Group, {
    fullHeight: true,
    align: "center",
    valign: "center",
    className: "st_header__propertylogo"
  }, trigger))));
}
Property.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /**
   * Component that renders the SVG logo, e.g. `IconWsj`, `IconBarrons`, etc
   * Use if the logo you need does not exist in Screentone.
   */
  image: PropTypes.any.isRequired,
  /**
   * nice name of current product, to be used for a11y, e.g. `Dow Jones`, `MarketWatch`, etc.
   * Use if you do not want to use the product name from the `options` array.
   */
  name: PropTypes.string.isRequired,
  /** Array of all options available. Each product should provide the following info:
   * `name`: name of product to display in dropdown, e.g. `MarketWatch`, `Dow Jones`
   * `value`: id of product, e.g. `marketwatch`, `dowjones`
   */
  options: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string
  })),
  /** callback when user switches product */
  onSwitch: PropTypes.func
};
Property.defaultProps = {
  className: null,
  componentEl: 'div',
  options: null,
  onSwitch: null
};
Property.displayName = 'Header.Property';

/**
User dropdown for header bar
*/
function UserDropdown({
  children,
  name,
  avatar,
  defaultOpen,
  onToggle
}) {
  const {
    open,
    componentRef,
    toggleDropdown
  } = useDropdownState({
    open: defaultOpen,
    onToggle
  });
  return /*#__PURE__*/React.createElement(Dropdown, {
    defaultOpen: defaultOpen,
    padding: {
      all: 'none'
    },
    position: "right",
    open: open,
    "aria-expanded": !!open,
    componentRef: componentRef,
    onToggle: toggleDropdown
  }, /*#__PURE__*/React.createElement(Dropdown.Trigger, null, /*#__PURE__*/React.createElement(Wrapper, {
    componentEl: "button",
    fullHeight: true,
    padding: {
      all: 'none'
    },
    className: ['st_focusable st_header__usertrigger', open ? 'st_header__usertrigger--active' : ''].join(' ')
  }, /*#__PURE__*/React.createElement(Avatar, {
    avatar: avatar,
    size: "lg",
    initials: name && name.charAt(0) || ' ',
    "aria-label": "name"
  }))), /*#__PURE__*/React.createElement(Dropdown.Content, {
    className: "st_header__dropdown st_header__dropdown--user"
  }, children));
}
UserDropdown.propTypes = {
  /** whether dropdown is open or closed by default */
  defaultOpen: PropTypes.bool,
  /** user name to display */
  name: PropTypes.string,
  /** avatar of user, if any */
  avatar: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  /** callback when dropdown is opened/closed */
  onToggle: PropTypes.func
};
UserDropdown.defaultProps = {
  defaultOpen: false,
  name: null,
  avatar: null,
  onToggle: null
};
UserDropdown.displayName = 'Header.User.Dropdown';

/**
Sub-component to display avatar and user info in the dropdown
*/
function UserNavUser({
  avatar,
  name,
  email,
  loginSource,
  onLogout
}) {
  if (!name) return null;
  const multipleSections = onLogout || loginSource;
  return /*#__PURE__*/React.createElement(Wrapper, {
    padding: {
      bottom: 'md'
    }
  }, /*#__PURE__*/React.createElement(Group, {
    direction: "column",
    gap: "md"
  }, /*#__PURE__*/React.createElement(Wrapper, {
    padding: {
      top: 'smd',
      horizontal: 'md'
    }
  }, /*#__PURE__*/React.createElement(Group, {
    direction: "row",
    wrap: false,
    gap: "sm"
  }, /*#__PURE__*/React.createElement(Avatar, {
    size: "xl",
    avatar: avatar,
    initials: name.charAt(0)
  }), /*#__PURE__*/React.createElement(Group.Item, null, /*#__PURE__*/React.createElement(Typography, {
    weight: "bold",
    size: "md",
    truncate: true
  }, name), email && /*#__PURE__*/React.createElement(Typography, {
    size: "sm",
    color: "asphalt",
    truncate: true
  }, email)))), multipleSections && /*#__PURE__*/React.createElement(Responsive, {
    md: true,
    lg: true
  }, /*#__PURE__*/React.createElement(Divider, {
    margin: {
      all: 'none'
    },
    fullWidth: true
  })), onLogout && /*#__PURE__*/React.createElement(Button, {
    secondary: true,
    onClick: onLogout,
    margin: {
      horizontal: 'md'
    }
  }, "Log Out"), loginSource && /*#__PURE__*/React.createElement(Wrapper, {
    padding: {
      horizontal: 'md',
      bottom: 'sm'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    size: "sm",
    color: "asphalt"
  }, "You are signed in via ", typeof loginSource === 'string' ? loginSource : /*#__PURE__*/React.createElement(Typography, {
    variant: "link",
    inline: true,
    componentEl: "a",
    href: loginSource.url,
    rel: "noopener noreferrer",
    target: "_blank"
  }, loginSource.text)))));
}
UserNavUser.propTypes = {
  /** url to avatar */
  avatar: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  /** name to display */
  name: PropTypes.string,
  /** email address, if any */
  email: PropTypes.string,
  /** event handler for logout button. Also controls whether button is shown */
  onLogout: PropTypes.func,
  /**
   * login source, if any, This can be a string with the source name or an
   * object with `text` of the login source name, `url` to link to the login
   * source
   * */
  loginSource: PropTypes.oneOfType([PropTypes.shape({
    text: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired
  }), PropTypes.string])
};
UserNavUser.defaultProps = {
  avatar: null,
  name: null,
  email: null,
  loginSource: null,
  onLogout: null
};
UserNavUser.displayName = 'Header.User.User';

/**
User component for header bar
displays either just the avatar
or the avatar + dropdown
*/
function User({
  children,
  name,
  avatar,
  className,
  ...otherProps
}) {
  const {
    desktop,
    mobile
  } = useHeaderBreakpoints();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Responsive, mobile, /*#__PURE__*/React.createElement("div", _extends({
    className: addClassNames(className, ['st_header__user']).join(' ')
  }, otherProps), children, !children && (name || avatar) && /*#__PURE__*/React.createElement(UserNavUser, {
    name: name,
    avatar: avatar
  }))), /*#__PURE__*/React.createElement(Responsive, desktop, /*#__PURE__*/React.createElement("div", _extends({
    className: addClassNames(className, ['st_header__item', 'st_header__user']).join(' ')
  }, otherProps), children && /*#__PURE__*/React.createElement(UserDropdown, {
    avatar: avatar,
    name: name
  }, children), !children && /*#__PURE__*/React.createElement(Avatar, {
    avatar: avatar,
    initials: name && name.charAt(0),
    size: "lg",
    "aria-label": name
  }))));
}
User.propTypes = {
  /** user name to display */
  name: PropTypes.string,
  /** avatar of user, if any */
  avatar: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  /** extra CSS classes, if any */
  className: classNamePropTypes
};
User.defaultProps = {
  name: null,
  avatar: null,
  className: null
};
User.User = UserNavUser;
User.Dropdown = UserDropdown;
User.displayName = 'Header.User';

function NotificationsFooter({
  children,
  className,
  onClick,
  closeUI,
  ...otherProps
}) {
  let cssClasses = [];
  cssClasses = addClassNames(className, cssClasses);

  // need to short-circuit click bc we need to close modal/dropdown on click
  const handleClick = useCallback(e => {
    closeUI();
    if (typeof onClick === 'function') onClick(e);
  }, [onClick, closeUI]);
  return /*#__PURE__*/React.createElement(Button, _extends({
    tertiary: true,
    className: cssClasses.join(' '),
    onClick: handleClick
  }, otherProps), children);
}
NotificationsFooter.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** callback to close ui on click, either the dropdown or the modal */
  closeUI: PropTypes.func,
  /** click handler, if any */
  onClick: PropTypes.func
};
NotificationsFooter.defaultProps = {
  className: null,
  onClick: null,
  closeUI: () => null
};
NotificationsFooter.displayName = 'Header.Notifications.Footer';

function NotificationsItem({
  componentEl,
  avatar,
  timestamp,
  initials,
  children,
  highlight,
  onClick,
  closeUI,
  ...otherProps
}) {
  // need to short-circuit click bc we need to close modal/dropdown on click
  const handleClick = useCallback(e => {
    closeUI();
    if (typeof onClick === 'function') onClick(e);
  }, [onClick, closeUI]);
  return /*#__PURE__*/React.createElement(Notifications.Item, _extends({
    componentEl: componentEl,
    avatar: avatar,
    timestamp: timestamp,
    initials: initials,
    highlight: highlight,
    onClick: handleClick
  }, otherProps), children);
}
NotificationsItem.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** url to avatar, if available */
  avatar: PropTypes.string,
  /** timestamp for event */
  timestamp: PropTypes.string,
  /** whether component should look highlighted */
  highlight: PropTypes.bool,
  /** fallback initials for avatar */
  initials: PropTypes.string,
  /** clickHandler, if any */
  onClick: PropTypes.func,
  /** function to close UI, either the dropdown or the modal */
  closeUI: PropTypes.func
};
NotificationsItem.defaultProps = {
  componentEl: defaults,
  avatar: null,
  highlight: false,
  timestamp: null,
  initials: null,
  onClick: () => null,
  closeUI: () => null
};
NotificationsItem.displayName = 'Header.Notifications.Item';

const DropdownContext = /*#__PURE__*/createContext({});
function DropdownProvider({
  children,
  onToggle
}) {
  const {
    open,
    componentRef,
    openDropdown,
    closeDropdown,
    toggleDropdown
  } = useDropdownState({
    open: false,
    onToggle
  });
  const data = useMemo(() => ({
    open,
    openUI: openDropdown,
    closeUI: closeDropdown,
    toggleUI: toggleDropdown,
    componentRef
  }), [open, openDropdown, closeDropdown, toggleDropdown, componentRef]);
  return /*#__PURE__*/React.createElement(DropdownContext.Provider, {
    value: data
  }, children);
}
DropdownProvider.propTypes = {
  /** what we want Dropdown to call when open/close state changes */
  onToggle: PropTypes.func
};
DropdownProvider.defaultProps = {
  onToggle: null
};

/**
Notifications dropdown for header bar, for larger breakpoints
*/
function NotificationsDropdown({
  emptyMessage,
  title,
  trigger,
  items,
  footer
}) {
  const dropdownContext = useContext(DropdownContext);
  const {
    open,
    componentRef,
    toggleUI,
    closeUI
  } = dropdownContext;
  const itemsWithContext = React.Children.map(items, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    return /*#__PURE__*/React.cloneElement(child, {
      closeUI
    });
  });
  const footerWithContext = React.Children.map(footer, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    return /*#__PURE__*/React.cloneElement(child, {
      closeUI
    });
  });
  return /*#__PURE__*/React.createElement(Dropdown, {
    open: open,
    onToggle: toggleUI,
    padding: {
      all: 'none'
    },
    trigger: trigger,
    position: "right",
    componentRef: componentRef
  }, /*#__PURE__*/React.createElement(Box.Title, null, title), /*#__PURE__*/React.createElement(Box.Content, {
    padding: {
      all: 'none'
    },
    className: "st_notificationsdropdown"
  }, /*#__PURE__*/React.createElement("div", {
    className: "st_notificationsdropdown__content"
  }, /*#__PURE__*/React.createElement(Notifications, {
    emptyMessage: emptyMessage
  }, itemsWithContext)), footerWithContext.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Divider, {
    fullWidth: true,
    margin: {
      all: 'none'
    }
  }), /*#__PURE__*/React.createElement(Group, {
    direction: "row",
    align: footerWithContext.length > 1 ? 'space-between' : 'center',
    margin: {
      vertical: 'sm',
      horizontal: 'md'
    }
  }, footerWithContext))));
}
NotificationsDropdown.propTypes = {
  /** message to display when there are no notifications */
  emptyMessage: PropTypes.string,
  /** title for box */
  title: PropTypes.string,
  /** trigger for notifications modal */
  trigger: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /** notification items to display */
  items: PropTypes.node,
  /** notifications footer to display */
  footer: PropTypes.node
};
NotificationsDropdown.defaultProps = {
  title: 'Notifications',
  emptyMessage: null,
  items: null,
  footer: null
};
NotificationsDropdown.displayName = 'Header.Notifications.Dropdown';

const ModalContext = /*#__PURE__*/createContext({});
function ModalProvider({
  children,
  onToggle
}) {
  const {
    open,
    openModal: openUI,
    closeModal: closeUI
  } = useModalState({
    onToggle
  });
  const data = useMemo(() => ({
    open,
    openUI,
    closeUI
  }), [open, openUI, closeUI]);
  return /*#__PURE__*/React.createElement(ModalContext.Provider, {
    value: data
  }, children);
}
ModalProvider.propTypes = {
  /** function callback when modal is opened or closed */
  onToggle: PropTypes.func
};
ModalProvider.defaultProps = {
  onToggle: null
};

/**
Notifications modal for header bar, for mobile breakpoints
*/
function NotificationsModal({
  emptyMessage,
  title,
  trigger,
  items,
  footer,
  onToggle
}) {
  const modalContext = useContext(ModalContext);
  const {
    open,
    openUI,
    closeUI
  } = modalContext;
  const {
    renderNode
  } = useModalPortal();
  const {
    containerHeight
  } = useHeader();
  const handleOpen = useCallback(() => {
    onToggle(true);
    openUI();
  }, [onToggle, openUI]);
  const handleClose = useCallback(() => {
    onToggle(false);
    closeUI();
  }, [onToggle, closeUI]);
  const handleToggle = useCallback(() => {
    if (open) {
      handleClose();
    } else {
      handleOpen();
    }
  }, [handleOpen, handleClose, open]);
  const itemsWithContext = React.Children.map(items, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    return /*#__PURE__*/React.cloneElement(child, {
      closeUI: modalContext.closeUI
    });
  });
  const footerWithContext = React.Children.map(footer, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    return /*#__PURE__*/React.cloneElement(child, {
      closeUI: modalContext.closeUI
    });
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    tertiary: true,
    onClick: handleToggle
  }, trigger), open && /*#__PURE__*/React.createElement(Modal, {
    top: containerHeight ? `${containerHeight}px` : 'var(--st-header-notifications-modal-top)',
    fullWidth: true,
    renderNode: renderNode,
    focusTrap: false,
    className: "st_notificationsmodal",
    showDismiss: false,
    onDismiss: handleClose
  }, /*#__PURE__*/React.createElement(Box.Title, {
    className: "st_notificationsmodalheader"
  }, title), /*#__PURE__*/React.createElement(Box.Content, {
    padding: {
      all: 'none'
    },
    className: "st_notificationsmodal__list"
  }, /*#__PURE__*/React.createElement(Notifications, {
    emptyMessage: emptyMessage
  }, itemsWithContext)), footerWithContext.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Divider, {
    fullWidth: true,
    margin: {
      all: 'none'
    }
  }), /*#__PURE__*/React.createElement(Group, {
    direction: "row",
    align: footerWithContext.length > 1 ? 'space-between' : 'center',
    margin: {
      vertical: 'sm',
      horizontal: 'md'
    }
  }, footerWithContext))));
}
NotificationsModal.propTypes = {
  /** message to display when there are no notifications */
  emptyMessage: PropTypes.string,
  /** title for box */
  title: PropTypes.string,
  /** trigger for notifications modal */
  trigger: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /** notification items to display */
  items: PropTypes.node,
  /** notifications footer to display */
  footer: PropTypes.node,
  /** what to do when modal is opened or closed, returns final open state */
  onToggle: PropTypes.func
};
NotificationsModal.defaultProps = {
  title: 'Notifications',
  emptyMessage: null,
  items: null,
  footer: null,
  onToggle: () => null
};
NotificationsModal.displayName = 'Header.Notifications.Modal';

/**
notifications dropdown or modal for header bar
*/
function HeaderNotifications({
  on,
  emptyMessage,
  title,
  children,
  onToggle,
  className,
  ...otherProps
}) {
  let cssClasses = ['st_header__item', 'st_header__notifications'];
  cssClasses = addClassNames(className, cssClasses);
  const {
    desktop,
    mobile
  } = useHeaderBreakpoints();
  const trigger = /*#__PURE__*/React.createElement("div", {
    className: `st_header__notificationalert ${on && 'st_header__notificationalert--on'}`
  }, /*#__PURE__*/React.createElement(IconBell, {
    alt: "notifications",
    color: "ink"
  }));
  const items = React.Children.map(children, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    if (child?.type?.displayName === 'Header.Notifications.Item') {
      return child;
    }
    return null;
  });
  const footer = React.Children.map(children, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return null;
    }
    if (child?.type?.displayName === 'Header.Notifications.Footer') {
      return child;
    }
    return null;
  });
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cssClasses.join(' ')
  }, otherProps), /*#__PURE__*/React.createElement(Responsive, mobile, /*#__PURE__*/React.createElement(ModalProvider, {
    onToggle: onToggle
  }, /*#__PURE__*/React.createElement(NotificationsModal, {
    emptyMessage: emptyMessage,
    title: title,
    trigger: trigger,
    items: items,
    footer: footer
  }))), /*#__PURE__*/React.createElement(Responsive, desktop, /*#__PURE__*/React.createElement(DropdownProvider, {
    onToggle: onToggle
  }, /*#__PURE__*/React.createElement(NotificationsDropdown, {
    emptyMessage: emptyMessage,
    title: title,
    trigger: trigger,
    items: items,
    footer: footer
  }))));
}
HeaderNotifications.propTypes = {
  /** is the bell on? */
  on: PropTypes.bool,
  /** message to display when there are no notifications */
  emptyMessage: PropTypes.string,
  /** title for box */
  title: PropTypes.string,
  /** what to do when dropdown/modal is opened or closed, returns final open state */
  onToggle: PropTypes.func,
  /** extra CSS classes, if any */
  className: classNamePropTypes
};
HeaderNotifications.defaultProps = {
  title: 'Notifications',
  on: false,
  emptyMessage: null,
  onToggle: null,
  className: null
};
HeaderNotifications.displayName = 'Header.Notifications';
HeaderNotifications.Item = NotificationsItem;
HeaderNotifications.Footer = NotificationsFooter;

/**
Component to render arbitrary content in Header
*/
function Custom({
  className,
  children,
  label,
  ...otherProps
}) {
  const {
    desktop,
    mobile
  } = useHeaderBreakpoints();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Responsive, mobile, /*#__PURE__*/React.createElement(Wrapper, _extends({
    padding: {
      vertical: 'mlg'
    },
    className: addClassNames(className, []).join(' ')
  }, otherProps), label && /*#__PURE__*/React.createElement(Typography, {
    color: "ink",
    weight: "bold",
    margin: {
      horizontal: 'mlg',
      bottom: 'sm'
    }
  }, label), /*#__PURE__*/React.createElement(Wrapper, {
    padding: {
      horizontal: 'md'
    }
  }, children))), /*#__PURE__*/React.createElement(Responsive, desktop, /*#__PURE__*/React.createElement("div", _extends({
    className: addClassNames(className, ['st_header__custom st_header__item']).join(' ')
  }, otherProps), children)));
}
Custom.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** label for section in mobile drawer */
  label: PropTypes.string
};
Custom.defaultProps = {
  className: null,
  label: null
};
Custom.displayName = 'Header.Custom';

/**
 *
 * Component that puts a <Divider> between each child passed into it
 */
const ItemsWithDivider = ({
  children
}) => {
  const childrenWithDivider = [];
  const totalChildren = React.Children.count(children);
  React.Children.forEach(children, (child, i) => {
    childrenWithDivider.push(child);
    // eslint-disable-next-line react/no-array-index-key
    if (i < totalChildren - 1) childrenWithDivider.push( /*#__PURE__*/React.createElement(Divider, {
      fullWidth: true,
      margin: {
        all: 'none'
      },
      key: `divider-${i}`
    }));
  });
  return childrenWithDivider;
};
var ItemsWithDivider$1 = ItemsWithDivider;

/**
Component to render app name and environment
*/
const Apps = /*#__PURE__*/React.forwardRef(({
  className,
  componentEl: Component,
  children,
  open: defaultOpen,
  ...otherProps
}, ref) => {
  const {
    open,
    componentRef,
    toggleDropdown
  } = useDropdownState({
    open: defaultOpen
  });
  const {
    desktop,
    mobile
  } = useHeaderBreakpoints();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Responsive, mobile, /*#__PURE__*/React.createElement(Component, _extends({
    className: addClassNames(className, ['st_header__apps']).join(' ')
  }, otherProps, {
    ref: ref
  }), /*#__PURE__*/React.createElement(Wrapper, {
    padding: {
      vertical: 'mlg'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    color: "ink",
    weight: "bold",
    margin: {
      horizontal: 'mlg',
      bottom: 'sm'
    }
  }, "Newsroom Tools"), /*#__PURE__*/React.createElement(Wrapper, {
    padding: {
      horizontal: 'md'
    }
  }, children)))), /*#__PURE__*/React.createElement(Responsive, desktop, /*#__PURE__*/React.createElement(Component, _extends({
    className: addClassNames(className, ['st_header__apps st_header__item']).join(' ')
  }, otherProps, {
    ref: ref
  }), /*#__PURE__*/React.createElement(Dropdown, {
    position: "right",
    padding: {
      all: 'none'
    },
    open: open,
    "aria-expanded": !!open,
    componentRef: componentRef,
    onToggle: toggleDropdown
  }, /*#__PURE__*/React.createElement(Dropdown.Trigger, {
    "aria-label": "Apps"
  }, /*#__PURE__*/React.createElement(Button, {
    tertiary: true,
    componentEl: "button",
    active: open
  }, /*#__PURE__*/React.createElement(IconDotGrid, {
    color: "ink"
  }))), /*#__PURE__*/React.createElement(Dropdown.Content, {
    className: "st_header__dropdown st_header__dropdown--apps"
  }, /*#__PURE__*/React.createElement(ItemsWithDivider$1, null, children))))));
});
Apps.propTypes = {
  /** whether dropdown is initially open or closed */
  open: PropTypes.bool,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** dom element or component to use for outermost wrapper */
  componentEl: props
};
Apps.defaultProps = {
  className: null,
  componentEl: 'div',
  open: false
};
Apps.displayName = 'Header.Apps';
var Apps$1 = Apps;

/**
Settings dropdown for header bar
*/
function Settings$1({
  children,
  open: defaultOpen,
  className,
  ...otherProps
}) {
  const {
    open,
    componentRef,
    toggleDropdown
  } = useDropdownState({
    open: defaultOpen
  });
  const {
    desktop,
    mobile
  } = useHeaderBreakpoints();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Responsive, mobile, /*#__PURE__*/React.createElement(Wrapper, _extends({
    padding: {
      vertical: 'mlg'
    },
    className: addClassNames(className, ['st_header__settings']).join(' ')
  }, otherProps), /*#__PURE__*/React.createElement(Typography, {
    color: "ink",
    weight: "bold",
    margin: {
      horizontal: 'mlg',
      bottom: 'sm'
    }
  }, "Settings"), /*#__PURE__*/React.createElement(Wrapper, {
    padding: {
      horizontal: 'md'
    }
  }, children))), /*#__PURE__*/React.createElement(Responsive, desktop, /*#__PURE__*/React.createElement("div", _extends({
    className: addClassNames(className, ['st_header__item', 'st_header__settings']).join(' ')
  }, otherProps), /*#__PURE__*/React.createElement(Dropdown, {
    padding: {
      all: 'none'
    },
    open: open,
    position: "right",
    "aria-expanded": !!open,
    componentRef: componentRef,
    onToggle: toggleDropdown
  }, /*#__PURE__*/React.createElement(Dropdown.Trigger, {
    "aria-label": "Settings"
  }, /*#__PURE__*/React.createElement(Button, {
    tertiary: true,
    componentEl: "button",
    active: open
  }, /*#__PURE__*/React.createElement(IconGear, {
    color: "ink"
  }))), /*#__PURE__*/React.createElement(Dropdown.Content, {
    className: "st_header__dropdown st_header__dropdown--settings"
  }, /*#__PURE__*/React.createElement(ItemsWithDivider$1, null, children))))));
}
Settings$1.propTypes = {
  /** whether dropdown is open or closed */
  open: PropTypes.bool,
  /** extra CSS classes, if any */
  className: classNamePropTypes
};
Settings$1.defaultProps = {
  open: false,
  className: null
};
Settings$1.displayName = 'Header.Settings';

const footerContext = /*#__PURE__*/React.createContext();
function generateVersionItem({
  renderVersion,
  version
}) {
  if (!version) return null;
  if (renderVersion && typeof renderVersion === 'function') {
    return renderVersion({
      version,
      component: Token,
      props: {
        color: 'blurple',
        children: version?.version
      }
    });
  }
  return /*#__PURE__*/React.createElement(Token, {
    color: "blurple"
  }, version.version);
}

/**
Component to render app name and environment
*/
const Footer = ({
  className,
  children,
  credit,
  version,
  renderVersion,
  ...otherProps
}) => /*#__PURE__*/React.createElement(Wrapper, _extends({
  background: "page",
  padding: {
    horizontal: 'md',
    vertical: 'sm'
  }
}, otherProps), /*#__PURE__*/React.createElement(Group, {
  direction: "column",
  gap: "xs"
}, credit && /*#__PURE__*/React.createElement(Typography, {
  variant: "note"
}, credit), version && /*#__PURE__*/React.createElement(Group, {
  direction: "row",
  gap: "sm"
}, typeof version === 'string' ? /*#__PURE__*/React.createElement(Typography, {
  size: "sm"
}, "Version: Tool", generateVersionItem({
  version,
  renderVersion
})) : version.map(v => /*#__PURE__*/React.createElement(Typography, {
  key: v.name,
  size: "sm"
}, v.name, " ", generateVersionItem({
  version: v,
  renderVersion
})))), children));
Footer.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** env of app (for token) */
  credit: PropTypes.string,
  /** versions to display */
  version: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    version: PropTypes.string
  }))]),
  /** Override rendering of version element to add additional props
   * to render as link with href do:
   * ({ component: Component, props}) => {
   *   // component is the React component (convert to titlecase to use)
   *   // props is the props that the React component would have rendered with
   *   // to add additional props, you can use this pattern:
   *   // return <Component {...props} componentEl="a" href="#foobar" />
   * }
   */
  renderVersion: PropTypes.func
};
Footer.defaultProps = {
  className: null,
  credit: null,
  version: null,
  renderVersion: null
};
Footer.displayName = 'Header.Footer';
var Footer$1 = Footer;

/**
Settings dropdown for header bar
*/
function Settings({
  children,
  open: defaultOpen,
  className,
  ...otherProps
}) {
  const {
    open,
    componentRef,
    toggleDropdown
  } = useDropdownState({
    open: defaultOpen
  });
  const {
    desktop,
    mobile
  } = useHeaderBreakpoints();
  const footerContent = useContext(footerContext);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Responsive, mobile, /*#__PURE__*/React.createElement("div", _extends({
    className: addClassNames(className, ['st_header__info']).join(' ')
  }, otherProps), /*#__PURE__*/React.createElement(Wrapper, {
    padding: {
      vertical: 'mlg'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    color: "ink",
    weight: "bold",
    margin: {
      horizontal: 'mlg',
      bottom: 'sm'
    }
  }, "Information"), /*#__PURE__*/React.createElement(Wrapper, {
    padding: {
      horizontal: 'md'
    }
  }, children)))), /*#__PURE__*/React.createElement(Responsive, desktop, /*#__PURE__*/React.createElement("div", _extends({
    className: addClassNames(className, ['st_header__info', 'st_header__item']).join(' ')
  }, otherProps), /*#__PURE__*/React.createElement(Dropdown, {
    padding: {
      all: 'none'
    },
    open: open,
    position: "right",
    "aria-expanded": !!open,
    componentRef: componentRef,
    onToggle: toggleDropdown
  }, /*#__PURE__*/React.createElement(Dropdown.Trigger, {
    "aria-label": "Information"
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    tertiary: true,
    componentEl: "button",
    active: open
  }, /*#__PURE__*/React.createElement(IconInfo, {
    color: "ink"
  }))), /*#__PURE__*/React.createElement(Dropdown.Content, {
    className: "st_header__dropdown st_header__dropdown--info"
  }, /*#__PURE__*/React.createElement(ItemsWithDivider$1, null, footerContent ? [children, footerContent] : children))))));
}
Settings.propTypes = {
  /** whether dropdown is open or closed */
  open: PropTypes.bool,
  /** extra CSS classes, if any */
  className: classNamePropTypes
};
Settings.defaultProps = {
  open: false,
  className: null
};
Settings.displayName = 'Header.Info';

/**
 * Context for setting styles on Header.LinkGroup and Header.Link
 * so that the header can control appearance instead of needing app authors
 * add props for setting the correct colors/styles
 */
const LinksContext = /*#__PURE__*/createContext();
const defaultValue = {
  link: {
    type: 'ink'
  }
};
function LinksProvider({
  section,
  children
}) {
  const value = useMemo(() => {
    switch (section) {
      case 'info':
      case 'apps-meta':
        return {
          link: {
            type: 'blurple'
          }
        };
      case 'apps':
        return {
          link: {
            type: 'icon'
          }
        };
      default:
        return defaultValue;
    }
  }, [section]);
  return /*#__PURE__*/React.createElement(LinksContext.Provider, {
    value: value
  }, children);
}
LinksProvider.propTypes = {
  section: PropTypes.string
};
LinksProvider.defaultProps = {
  section: null
};
function useLinks() {
  const context = useContext(LinksContext);
  if (context === undefined) {
    // throw new Error('Header.Links must be used inside a Header section (Apps, Info, Settings)');
    return defaultValue;
  }
  return context;
}
function useLinkgroup(section) {
  switch (section) {
    case 'apps':
      return {
        variant: 'grid'
      };
    case 'theme':
      return {
        variant: 'flush'
      };
    default:
      return {
        variant: null
      };
  }
}

/**
Component to render app name and environment
*/
const Link = /*#__PURE__*/React.forwardRef(({
  className,
  componentEl: Component,
  children,
  icon: IconComponent,
  iconPosition,
  active,
  disabled,
  ...otherProps
}, ref) => {
  let cssClasses = ['st_headerlink', 'st_focusable', 'st_width--full'];
  if (active) cssClasses.push('st_headerlink--active');
  if (disabled) cssClasses.push('st_headerlink--disabled');
  cssClasses = addClassNames(className, cssClasses);
  const {
    link
  } = useLinks();
  const {
    desktop,
    mobile
  } = useHeaderBreakpoints();
  const additionalAttributes = {};
  if (Component === 'button' && disabled) {
    additionalAttributes.disabled = 'true';
  }
  const paddingDesktop = useMemo(() => getPadding({
    vertical: 'xs',
    horizontal: 'sm'
  }), []);
  const paddingMobile = useMemo(() => getPadding({
    vertical: 'smd',
    horizontal: 'sm'
  }), []);
  if (link?.type === 'icon') {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Responsive, desktop, /*#__PURE__*/React.createElement(Component, _extends({
      className: [`st_headerlink--${link?.type}`, ...paddingDesktop, ...cssClasses].join(' ')
    }, additionalAttributes, otherProps, {
      ref: ref
    }), /*#__PURE__*/React.createElement(Group, {
      direction: "column",
      gap: "sm",
      align: "center"
    }, IconComponent && /*#__PURE__*/React.createElement(IconComponent, {
      size: "lg",
      className: "st_linkgrid__icon"
    }), /*#__PURE__*/React.createElement(Typography, {
      color: "blurple",
      weight: "reg",
      align: "center",
      size: "inherit"
    }, children)))), /*#__PURE__*/React.createElement(Responsive, mobile, /*#__PURE__*/React.createElement(Component, _extends({
      className: ['st_headerlink--ink', ...paddingMobile, ...cssClasses].join(' ')
    }, additionalAttributes, otherProps, {
      ref: ref
    }), /*#__PURE__*/React.createElement(Group, {
      direction: "row",
      gap: "sm",
      align: "start"
    }, IconComponent && /*#__PURE__*/React.createElement(IconComponent, null), children))));
  }
  return /*#__PURE__*/React.createElement(Responsive, desktop, matches => {
    const finalPadding = matches ? paddingDesktop : paddingMobile;
    return /*#__PURE__*/React.createElement(Component, _extends({
      className: [`st_headerlink--${link?.type}`, ...finalPadding, ...cssClasses].join(' ')
    }, otherProps, {
      ref: ref
    }), /*#__PURE__*/React.createElement(Group, {
      direction: "row",
      gap: "sm",
      align: "start"
    }, IconComponent && iconPosition === 'left' && /*#__PURE__*/React.createElement(IconComponent, null), children, IconComponent && iconPosition === 'right' && /*#__PURE__*/React.createElement(IconComponent, null)));
  });
});
Link.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** icon, if any */
  icon: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  /** icon position */
  iconPosition: PropTypes.oneOf(['left', 'right']),
  /** whether link is active */
  active: PropTypes.bool,
  /** whether link is disabled */
  disabled: PropTypes.bool
};
Link.defaultProps = {
  className: null,
  componentEl: 'button',
  icon: null,
  iconPosition: 'left',
  active: false,
  disabled: false
};
Link.displayName = 'Header.Link';
var Link$1 = Link;

/**
Component to render app name and environment
*/
const LinkGroup = /*#__PURE__*/React.forwardRef(({
  className,
  componentEl: Component,
  children,
  label,
  section,
  ...otherProps
}, ref) => {
  let cssClasses = ['st_headerlinkgroup'];
  cssClasses = addClassNames(className, cssClasses);
  const {
    desktop,
    mobile
  } = useHeaderBreakpoints();
  const {
    variant
  } = useLinkgroup(section);
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps, {
    ref: ref
  }), /*#__PURE__*/React.createElement(Responsive, mobile, /*#__PURE__*/React.createElement(Wrapper, {
    padding: variant === 'flush' ? {
      horizontal: 'sm',
      vertical: 'smd'
    } : null
  }, /*#__PURE__*/React.createElement(Group, {
    direction: "column",
    align: "start",
    gap: "none",
    fullWidth: true,
    "aria-label": label
  }, /*#__PURE__*/React.createElement(LinksProvider, {
    section: section
  }, children)))), /*#__PURE__*/React.createElement(Responsive, desktop, /*#__PURE__*/React.createElement(Wrapper, {
    padding: variant === 'flush' ? {
      all: 'md'
    } : {
      vertical: 'md',
      horizontal: 'smd'
    }
  }, label && /*#__PURE__*/React.createElement(Typography, {
    color: "ink",
    weight: "bold",
    size: "sm",
    margin: {
      horizontal: 'sm',
      bottom: 'smd'
    }
  }, label), /*#__PURE__*/React.createElement(Group, {
    direction: variant === 'grid' ? 'row' : 'column',
    rowgap: variant === 'grid' ? 'md' : 'sm',
    align: "start",
    valign: "start",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(LinksProvider, {
    section: section
  }, children)))));
});
LinkGroup.propTypes = {
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** optional label for list of links */
  label: PropTypes.string,
  /** section for this link group */
  section: PropTypes.string
};
LinkGroup.defaultProps = {
  className: null,
  componentEl: 'div',
  label: null,
  section: null
};
LinkGroup.displayName = 'Header.LinkGroup';
var LinkGroup$1 = LinkGroup;

// internal component for handling mobile drawer
// broken out in order to add drawer-specific effects
const HeaderDrawer = ({
  items,
  breakpoints,
  status,
  drawerRef
}) => {
  useEffect(() => {
    if (status === 'beforeopen') {
      drawerRef?.current?.scrollTo(0, 0);
    }
  }, [status, drawerRef]);
  if (!items || items?.length <= 0) return null;
  return /*#__PURE__*/React.createElement(Responsive, breakpoints, /*#__PURE__*/React.createElement("div", {
    className: `st_drawer-overlay st_drawer-overlay--${status}`
  }, /*#__PURE__*/React.createElement("div", {
    className: `st_drawer st_drawer--${status}`,
    ref: drawerRef
  }, items.map((d, i) => /*#__PURE__*/React.createElement(React.Fragment, {
    key: d?.type?.displayName
  }, d, i < items.length - 1 ? /*#__PURE__*/React.createElement(Divider, {
    fullWidth: true,
    margin: {
      all: 'none'
    }
  }) : null)))));
};
HeaderDrawer.displayName = 'Header.Drawer';
HeaderDrawer.propTypes = {
  /** array of items to render */
  items: PropTypes.arrayOf(PropTypes.any),
  /** breakpoints to display component */
  breakpoints: PropTypes.shape({}),
  /** status of drawer */
  status: PropTypes.string.isRequired,
  /** ref of drawer component */
  drawerRef: PropTypes.any.isRequired
};
HeaderDrawer.defaultProps = {
  items: [],
  breakpoints: {}
};
var HeaderDrawer$1 = HeaderDrawer;

const drawerSections = ['Header.Property', 'Header.Custom', 'Header.Apps', 'Header.Settings', 'Header.Info', 'Header.User', 'Header.Footer'];
const HeaderSections = ['Header.Property', 'Header.Name', 'Header.Info', 'Header.Settings', 'Header.Notifications', 'Header.Apps', 'Header.User'];
const Header = /*#__PURE__*/React.forwardRef(({
  componentEl: Component,
  children,
  className,
  margin,
  zIndex,
  before,
  after,
  sticky,
  ...otherProps
}, ref) => {
  let cssClasses = ['st_width--full'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addMarginClasses(margin, cssClasses);
  // whether header has generated height yet
  const [hasHeight, setHasHeight] = React.useState(false);

  // keep track of height
  const [headerSize, setSize] = React.useState();

  // hook for keeping track of mobile drawer
  const {
    isDrawerOpen,
    drawerStatus,
    drawerRef,
    toggleDrawer,
    setContainerHeight // need to pass height to context so rest of app can consume
  } = useHeader();

  // hook for keeping track of mobile drawer
  const containerRef = useRef(null);
  React.useEffect(() => {
    const newSize = containerRef?.current?.getBoundingClientRect();
    setSize(newSize?.height);
    setHasHeight(true);
  }, [containerRef, setContainerHeight]);

  // update height when header container changes height
  useResizeObserver(containerRef, entry => {
    setSize(entry.contentRect?.height);
  });

  // let context know whenever header height changes
  React.useEffect(() => {
    setContainerHeight(headerSize);
  }, [headerSize, setContainerHeight]);
  const headerItemsSm1 = [];
  const headerItemsSm2 = [];
  const drawerItemsSm = [];
  const headerItemsLg1 = [];
  const headerItemsLg2 = [];
  let footerItem = null;

  // separate between items that need to be added to the header
  // and items that need to be added in the header
  // for mobile
  React.Children.forEach(children, child => {
    if (!child?.type?.displayName) return;
    if (sticky) {
      if (child?.type?.displayName === 'Header.Name') {
        headerItemsSm1.push(child);
      } else if (child?.type?.displayName === 'Header.Notifications') {
        headerItemsSm2.push(child);
      } else {
        drawerItemsSm.push(child);
      }
    }
    if (['Header.Property', 'Header.Name'].includes(child?.type?.displayName)) {
      headerItemsLg1.push(child);
    } else if (child?.type?.displayName !== 'Header.Footer') {
      headerItemsLg2.push(child);
    }
    if (child?.type?.displayName === 'Header.Footer') {
      // get info about footer here
      footerItem = child;
    }
  });

  // put in correct order for mobile breakpoint
  // we aren't using grids in mobile breakpoints rn
  // because it messes with the Divider instances we need to render lines between sections
  if (drawerItemsSm.length > 1) {
    drawerItemsSm.sort((a, b) => drawerSections.indexOf(a?.type?.displayName) - drawerSections.indexOf(b?.type?.displayName));
  }
  headerItemsLg1.sort((a, b) => HeaderSections.indexOf(a?.type?.displayName) - HeaderSections.indexOf(b?.type.displayName));
  headerItemsLg2.sort((a, b) => HeaderSections.indexOf(a?.type?.displayName) - HeaderSections.indexOf(b?.type?.displayName));

  // if header is sticky, show mobile layout with drawer
  // if header is not sticky, only show desktop layout at all breakpoints
  const mobileBreakpoints = useMemo(() => sticky ? {
    xs: true
  } : {}, [sticky]);
  const desktopBreakpoints = useMemo(() => sticky ? {
    sm: true,
    lg: true
  } : {
    xs: true,
    lg: true
  }, [sticky]);
  const headerJSX = /*#__PURE__*/React.createElement(HeaderBreakpointsProvider, {
    desktop: desktopBreakpoints,
    mobile: mobileBreakpoints
  }, /*#__PURE__*/React.createElement("div", {
    ref: containerRef
  }, before, /*#__PURE__*/React.createElement(Component, _extends({}, otherProps, {
    className: cssClasses.join(' '),
    ref: ref
  }, otherProps), /*#__PURE__*/React.createElement("nav", {
    className: "st_header",
    "aria-label": "Main navigation"
  }, /*#__PURE__*/React.createElement(Responsive, mobileBreakpoints, /*#__PURE__*/React.createElement(Group, {
    fullWidth: true,
    fullHeight: true,
    align: "space-between",
    gap: "sm"
  }, /*#__PURE__*/React.createElement(Group, {
    fullHeight: true,
    gap: "none"
  }, drawerItemsSm.length >= 1 && /*#__PURE__*/React.createElement("div", {
    className: ['st_header__property', 'st_header__item'].join(' ')
  }, /*#__PURE__*/React.createElement(Wrapper, {
    onClick: toggleDrawer,
    componentEl: "button",
    "aria-label": "toggle menu",
    fullHeight: true,
    className: "st_focusable st_header__hoverable st_header__drawericon"
  }, /*#__PURE__*/React.createElement(IconMenu, {
    size: "md"
  }))), headerItemsSm1), headerItemsSm2)), /*#__PURE__*/React.createElement(Responsive, desktopBreakpoints, /*#__PURE__*/React.createElement(footerContext.Provider, {
    value: footerItem
  }, /*#__PURE__*/React.createElement(Group, {
    fullWidth: true,
    fullHeight: true,
    align: "space-between",
    gap: "sm"
  }, /*#__PURE__*/React.createElement(Group, {
    gap: "none",
    fullHeight: true
  }, headerItemsLg1), /*#__PURE__*/React.createElement(Group, {
    gap: "sm",
    fullHeight: true
  }, headerItemsLg2)))))), after), /*#__PURE__*/React.createElement(HeaderDrawer$1, {
    items: drawerItemsSm,
    breakpoints: mobileBreakpoints,
    status: drawerStatus,
    drawerRef: drawerRef
  }));
  if (!sticky) return headerJSX;
  return /*#__PURE__*/React.createElement(Sticky, {
    fullWidth: true,
    disabled: !hasHeight,
    placeholder: /*#__PURE__*/React.createElement("div", {
      style: {
        height: headerSize || 0
      }
    }),
    type: "fixed",
    zIndex: zIndex,
    fullHeight: isDrawerOpen,
    className: "st_headerstickywrapper"
  }, headerJSX);
});
Header.displayName = 'Header';
Header.Name = Name$1;
Header.Property = Property;
Header.User = User;
Header.Notifications = HeaderNotifications;
Header.Custom = Custom;
Header.Apps = Apps$1;
Header.Settings = Settings$1;
Header.Info = Settings;
Header.Link = Link$1;
Header.LinkGroup = LinkGroup$1;
Header.Footer = Footer$1;
Header.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** margin for headerbar, if any */
  margin: marginPropTypes,
  /**
   * z-index for sticky header container.
   * One of `minor`, `mid`, `mad`, `major`, `top` */
  zIndex: zIndexProps,
  /** this JSX will appear above the header and will be sticky */
  before: PropTypes.node,
  /** this JSX will appear below the header and will be sticky */
  after: PropTypes.node,
  /** whether header is sticky. Disabling it also disables mobile drawer */
  sticky: PropTypes.bool
};
Header.defaultProps = {
  componentEl: 'header',
  className: null,
  margin: null,
  zIndex: 'major',
  before: null,
  after: null,
  sticky: true
};
var Header$1 = Header;

export { Header$1 as H, HeaderProvider as a, useHeader as u };
